// components/SubscriptionManagement.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import Errors from '../../shared/errors';
import { ResumeSubscription } from '../resume-subscription/ducks/reducer';
import Loader from '../../shared/loader';
import InfoText from '../../shared/info-text';
import InfoTextBox from '../../shared/info-text-box';


interface SubscriptionManagementProps {
    // paymentStatus: 'Pending' | 'Paid' | 'Paused' | 'Cancelled' | 'Failed';
    // nextBillingDate?: string;
    // pricingPlan?: string;
    errors?: any;
    resuming: boolean;
    resumeComplete: boolean;
    onResumeSubscription: () => Promise<void>;
    loading: boolean;
    subscription?: ResumeSubscription | null;
    requiresPaymentMethodUpdate: boolean;
    message: string | null;
    stripeBillingPortalUrl?: string | null;
}

const ResumeSubscriptionForm: React.FC<SubscriptionManagementProps> = ({ errors, onResumeSubscription, resuming, resumeComplete, loading, subscription, requiresPaymentMethodUpdate, message, stripeBillingPortalUrl }) => {
    const [isResuming, setIsResuming] = useState(false);
    const [isWaitingForPaymentUpdate, setIsWaitingForPaymentUpdate] = useState(false);
    const [paymentUpdateTimeout, setPaymentUpdateTimeout] = useState<NodeJS.Timeout | null>(null);
    const [cardInfo, setCardInfo] = useState<{
        last4?: string;
        brand?: string;
        expiryMonth?: number;
        expiryYear?: number;
    }>({});

    const openBillingPortal = async () => {
        try {
            // For now, redirect to account page where billing portal link is available
            // TODO: Fetch billing portal URL directly from API
            if (stripeBillingPortalUrl) {
                window.location.href = stripeBillingPortalUrl;
            } else {
                window.location.href = '/account';
            }
        } catch (error) {
            toast.error('Failed to open billing portal. Please try again.');
        }
    };

    // Check if returning from Stripe on component mount
    // useEffect(() => {
    //     const isUpdating = localStorage.getItem('updatingPaymentMethod') === 'true';
    //     const startTime = localStorage.getItem('paymentUpdateStartTime');

    //     if (isUpdating) {
    //         setIsWaitingForPaymentUpdate(true);

    //         const elapsed = startTime ? Date.now() - parseInt(startTime) : 0;

    //         if (elapsed < 5000) {
    //             toast.info('Processing your payment method update...');
    //         } else if (elapsed < 15000) {
    //             toast.info('Still processing... This usually takes a few seconds.');
    //         } else {
    //             toast.warn('Taking longer than usual. Please wait...');
    //         }

    //         // Set timeout for 30 seconds
    //         const timeout = setTimeout(() => {
    //             handlePaymentUpdateTimeout();
    //         }, 30000 - elapsed);

    //         setPaymentUpdateTimeout(timeout);
    //     }

    //     return () => {
    //         if (paymentUpdateTimeout) {
    //             clearTimeout(paymentUpdateTimeout);
    //         }
    //     };
    // }, []);

    // SignalR event handlers
    const handlePaymentMethodUpdated = useCallback((event: any) => {
        // Clear timeout
        if (paymentUpdateTimeout) {
            clearTimeout(paymentUpdateTimeout);
            setPaymentUpdateTimeout(null);
        }

        // Clear localStorage
        const attemptedAction = localStorage.getItem('attemptedAction');
        localStorage.removeItem('updatingPaymentMethod');
        localStorage.removeItem('paymentUpdateStartTime');
        localStorage.removeItem('attemptedAction');

        // Update state
        setIsWaitingForPaymentUpdate(false);
        setCardInfo({
            last4: event.last4,
            brand: event.brand,
            expiryMonth: event.expiryMonth,
            expiryYear: event.expiryYear,
        });

        // Show success message
        toast.success(
            `✅ Payment method updated!\n${event.brand} ending in ${event.last4}\nExpires: ${event.expiryMonth}/${event.expiryYear}`,
            { autoClose: 5000 }
        );

        // If user was trying to resume, prompt them
        if (attemptedAction === 'resume') {
            setTimeout(() => {
                promptResumeAfterUpdate();
            }, 2000);
        }
    }, [paymentUpdateTimeout]);

    const handlePaymentUpdateTimeout = () => {
        setIsWaitingForPaymentUpdate(false);
        localStorage.removeItem('updatingPaymentMethod');
        localStorage.removeItem('paymentUpdateStartTime');
        localStorage.removeItem('attemptedAction');

        toast.error(
            'Payment method update confirmation timed out. ' +
            'Your card may have been updated. Please check your billing settings or try again.',
            { autoClose: 10000 }
        );
    };

    const promptResumeAfterUpdate = () => {
        // You can use a modal library here, or just show a toast
        const shouldResume = window.confirm(
            'Your payment method has been updated. Would you like to resume your subscription now?'
        );

        if (shouldResume) {
            handleResumeSubscription(false);
        }
    };

    const handleResumeSubscription = async (resetBilling: boolean) => {
        setIsResuming(true);

        try {
            //   const response = await paymentService.resumeSubscription({
            //     resetBillingCycle: resetBilling,
            //   });

            //   if (!response.success) {
            //     if (response.requiresPaymentMethodUpdate) {
            //       // Card needs updating - redirect to Stripe
            //       await redirectToUpdatePaymentMethod();
            //     } else {
            //       const errorMessage = response.errors?.['stripe']?.[0] || 'Failed to resume subscription';
            //       toast.error(errorMessage);
            //     }
            //   } else {
            //     toast.success(response.message || 'Subscription resuming...');
            //     // UI will update when webhook confirms via SignalR
            //   }
        } catch (error) {
            toast.error('Failed to resume subscription. Please try again.');
        } finally {
            setIsResuming(false);
        }
    };

    const redirectToUpdatePaymentMethod = async () => {
        try {
            //   const confirmed = window.confirm(
            //     'Your payment method needs to be updated. You will be redirected to Stripe.\n\n' +
            //     'After updating your card, you will be returned here automatically.'
            //   );

            //   if (!confirmed) return;

            //   // Store state in localStorage
            //   localStorage.setItem('updatingPaymentMethod', 'true');
            //   localStorage.setItem('attemptedAction', 'resume');
            //   localStorage.setItem('paymentUpdateStartTime', Date.now().toString());

            //   // Create portal session
            //   const portalUrl = await paymentService.createBillingPortalSession(
            //     window.location.href, // Return to current page
            //     true // Focus on payment method update
            //   );

            //   // Redirect to Stripe (same window)
            //   window.location.href = portalUrl;
        } catch (error) {
            toast.error('Failed to create billing portal session');
        }
    };


    // Render different UI based on payment status
    // if (paymentStatus !== 'Paused') {
    //     return (
    //         <div className="subscription-active">
    //             <h3>Active Subscription</h3>
    //             <p>Plan: {pricingPlan}</p>
    //             <p>Next billing: {nextBillingDate}</p>

    //             <button onClick={openBillingPortal} className="btn-secondary">
    //                 Manage Billing
    //             </button>
    //         </div>
    //     );
    // }

    return (
        <div className="subscription-paused">
            {loading && <Loader />}
            <div className="menu-label">
                <span>Subscription Paused</span>
            </div>
            <div>
                <p>Your subscription is currently paused. Click the button below to resume your subscription and restore access to your account.</p>

                {subscription && (
                    <div className="mt-2 mb-2">
                        <h4 className="title is-6">Subscription Details</h4>
                        <div className="content">
                            <p><strong>Plan:</strong> {subscription.pricingPlan}</p>
                            {subscription.price !== undefined && subscription.currencySymbol && (
                                <p><strong>Price:</strong> <span dangerouslySetInnerHTML={{ __html: subscription.currencySymbol }}></span>{subscription.price} per month</p>
                            )}
                            <p><strong>Paused Date:</strong> {new Date(subscription.pausedDate).toLocaleDateString()}</p>
                            <p><strong>Next Billing Date:</strong> {new Date(subscription.billingDate).toLocaleDateString()}</p>
                            {subscription.passedBillingDate && (
                                <p className="has-text-warning"><strong>Note:</strong> Billing date has passed. Resuming will charge immediately.</p>
                            )}
                        </div>
                    </div>
                )}

                {requiresPaymentMethodUpdate && (
                    <div className="notification is-warning mt-2">
                        <p className="has-text-weight-bold">⚠️ Payment Method Issue</p>
                        <p className="mt-1">
                            We couldn't process your payment. This might be because your card has expired,
                            was declined, or needs to be updated.
                        </p>
                        <p className="mt-2">
                            Please update your payment method to resume your subscription.
                        </p>
                        <p className='mt-2'>
                            Once updated in Stripe click the Return to Filmanize button on the left to come back here.
                        </p>
                        <div className="buttons mt-2">
                            <button className="button is-primary" onClick={openBillingPortal}>
                                Update Payment Method
                            </button>
                        </div>
                    </div>
                )}

                <Errors errors={errors} />
                {!requiresPaymentMethodUpdate &&
                    <div className='buttons mt-2'>
                        <button className={`button is-primary ${resuming ? 'is-loading' : ''}`} onClick={onResumeSubscription} disabled={resuming}>
                            {resuming ? 'Resuming...' : 'Resume Subscription'}
                        </button>
                        {message && <InfoTextBox className="mt-2">{message}</InfoTextBox>}
                    </div>}
            </div>
            {/* <Errors errors={errors} />
            Waiting for payment update confirmation
            {isWaitingForPaymentUpdate && (
                <div className="payment-update-waiting">
                    <div className="spinner" />
                    <p className="text-muted">
                        Waiting for payment method confirmation...
                    </p>
                    <small className="text-muted">
                        This usually takes a few seconds. Please don't refresh the page.
                    </small>
                </div>
            )} */}

            {/* Show card info after successful update */}
            {/* {cardInfo.last4 && !isWaitingForPaymentUpdate && (
                <div className="alert alert-success">
                    <strong>✅ Payment method updated successfully!</strong>
                    <p>
                        {cardInfo.brand} ending in {cardInfo.last4}
                        <br />
                        Expires: {cardInfo.expiryMonth}/{cardInfo.expiryYear}
                    </p>
                </div>
            )} */}

            {/* Resume options */}
            {/* {!isWaitingForPaymentUpdate && (
                <div className="resume-options">
                    <p className="mb-3">Resume your subscription to restore access:</p>

                    <div className="d-grid gap-2">
                        <button
                            onClick={() => handleResumeSubscription(false)}
                            disabled={isResuming}
                            className="btn btn-primary"
                        >
                            {isResuming ? 'Resuming...' : `Resume on ${nextBillingDate || 'original date'}`}
                        </button>

                        <button
                            onClick={() => handleResumeSubscription(true)}
                            disabled={isResuming}
                            className="btn btn-secondary"
                        >
                            {isResuming ? 'Resuming...' : 'Resume now and pay immediately'}
                        </button>
                    </div>

                    <div className="mt-3">
                        <button onClick={openBillingPortal} className="btn btn-link">
                            Update payment method
                        </button>
                    </div>
                </div>
            )} */}
        </div>
    );
};

export default ResumeSubscriptionForm;